// Brewing brew-graph (MB.4). PURE (ENG-1): the ingredient→potion transition table
// a brewing stand walks. Water + nether wart → awkward; awkward + a primary
// ingredient → a base potion; redstone extends, glowstone strengthens, and a
// fermented spider eye corrupts a potion into its opposite. Splash/lingering are
// a separate item transform (MB.4-remainder). `reachable()` proves every intended
// potion is brewable from water, so the brewing economy is closed.

/** A single ingredient→potion step (the "primary" brew edges). */
export interface BrewStep { from: string; ingredient: string; to: string; }

/** Primary brews: water→awkward, then awkward→each base potion. */
export const BREW_STEPS: readonly BrewStep[] = [
  { from: 'water', ingredient: 'nether_wart', to: 'awkward' },
  { from: 'awkward', ingredient: 'sugar', to: 'swiftness' },
  { from: 'awkward', ingredient: 'blaze_powder', to: 'strength' },
  { from: 'awkward', ingredient: 'ghast_tear', to: 'regeneration' },
  { from: 'awkward', ingredient: 'magma_cream', to: 'fire_resistance' },
  { from: 'awkward', ingredient: 'spider_eye', to: 'poison' },
  { from: 'awkward', ingredient: 'rabbit_foot', to: 'leaping' },
  { from: 'awkward', ingredient: 'glistering_melon_slice', to: 'healing' },
  { from: 'awkward', ingredient: 'pufferfish', to: 'water_breathing' },
];

/** Modifier ingredients that transform an existing potion. */
export const REDSTONE = 'redstone';       // → extended duration (same effect key)
export const GLOWSTONE = 'glowstone_dust'; // → level II ("_2" variant)
export const FERMENTED = 'fermented_spider_eye'; // → corrupted opposite

/** Fermented-spider-eye corruptions (Java's inversion table, our subset). */
const CORRUPT: Record<string, string> = {
  swiftness: 'slowness',
  leaping: 'slowness',
  healing: 'harming',
  poison: 'harming',
  night_vision: 'invisibility',
  water_breathing: 'harming',
};

/** Potions that have a brewed level-II ("strong") form. */
const HAS_STRONG = new Set(['swiftness', 'strength', 'leaping', 'healing', 'harming', 'poison', 'regeneration']);

/**
 * Brew one step: the potion currently in the bottle + the ingredient → the next
 * potion key, or null if that pairing brews nothing. Modifiers act on any potion.
 */
export function brew(potion: string, ingredient: string): string | null {
  // Modifiers apply once: never to water/awkward, and never re-extend/re-strengthen
  // an already-modified potion (keeps the brew graph finite).
  const modifiable = potion !== 'water' && potion !== 'awkward' && !potion.endsWith('_long') && !potion.endsWith('_2');
  if (ingredient === GLOWSTONE) return modifiable && HAS_STRONG.has(potion) ? `${potion}_2` : null;
  if (ingredient === REDSTONE) return modifiable ? `${potion}_long` : null;
  if (ingredient === FERMENTED) return CORRUPT[potion] ?? null;
  const step = BREW_STEPS.find((s) => s.from === potion && s.ingredient === ingredient);
  return step ? step.to : null;
}

/** Ticks a brewing stand takes to finish one batch (Java 400 gt = 20 s). */
export const BREW_TICKS = 400;

/** Brew all three bottles at once with one ingredient (a stand brews the batch
 *  together). A slot that can't brew (empty, or no valid pairing) is left as-is. */
export function brewBatch(bottles: readonly (string | null)[], ingredient: string): (string | null)[] {
  return bottles.map((b) => (b == null ? null : brew(b, ingredient) ?? b));
}

/** Whether at least one bottle in the stand transforms with this ingredient — the
 *  gate for the stand to start (and consume fuel + ingredient). */
export function canBrew(bottles: readonly (string | null)[], ingredient: string): boolean {
  return bottles.some((b) => b != null && brew(b, ingredient) !== null);
}

/** Every potion reachable from `start` (default "water") over the brew graph. */
export function reachable(start = 'water'): Set<string> {
  const seen = new Set<string>([start]);
  const queue = [start];
  const ingredients = [...new Set(BREW_STEPS.map((s) => s.ingredient)), GLOWSTONE, REDSTONE, FERMENTED];
  while (queue.length) {
    const p = queue.shift()!;
    for (const ing of ingredients) {
      const next = brew(p, ing);
      if (next && !seen.has(next)) { seen.add(next); queue.push(next); }
    }
  }
  return seen;
}
